import {FabricImage, classRegistry} from 'fabric';
import type {ImageProps} from 'fabric';

const SPRITE_WIDTH = 40;
const SPRITE_HEIGHT = 40;
const FRAME_TIME = 100;

export class Sprite extends FabricImage {
    static type = 'sprite';

    spriteWidth = SPRITE_WIDTH;
    spriteHeight = SPRITE_HEIGHT;
    spriteIndex = 0;
    frameTime = FRAME_TIME;
    onPlay?: () => void;

    private tmpCanvasEl!: HTMLCanvasElement;
    private spriteImages: HTMLImageElement[] = [];
    private animInterval?: ReturnType<typeof setInterval>;

    constructor(element: HTMLImageElement, options?: Partial<ImageProps>) {
        super(element, {
            selectable: false,
            evented: false,
            ...options,
            width: SPRITE_WIDTH,
            height: SPRITE_HEIGHT
        });
        this.createTmpCanvas();
        this.createSpriteImages();
    }

    private createTmpCanvas(): void {
        this.tmpCanvasEl = document.createElement('canvas');
        this.tmpCanvasEl.width = this.spriteWidth || this.width;
        this.tmpCanvasEl.height = this.spriteHeight || this.height;
    }

    private createSpriteImages(): void {
        this.spriteImages = [];
        const steps = this._element.width / this.spriteWidth;
        for (let i = 0; i < steps; i++) {
            this.createSpriteImage(i);
        }
    }

    private createSpriteImage(i: number): void {
        const tmpCtx = this.tmpCanvasEl.getContext('2d');
        if (!tmpCtx) {
            return;
        }
        tmpCtx.clearRect(0, 0, this.tmpCanvasEl.width, this.tmpCanvasEl.height);
        tmpCtx.drawImage(this._element, -i * this.spriteWidth, 0);
        const dataURL = this.tmpCanvasEl.toDataURL('image/png');
        const tmpImg = new Image();
        tmpImg.src = dataURL;
        this.spriteImages.push(tmpImg);
    }

    _render(ctx: CanvasRenderingContext2D): void {
        const spriteImage = this.spriteImages[this.spriteIndex];
        if (!spriteImage) {
            return;
        }
        ctx.drawImage(spriteImage, -this.width / 2, -this.height / 2);
    }

    play(): void {
        this.animInterval = setInterval(() => {
            if (this.onPlay) {
                this.onPlay();
            }
            this.dirty = true;
            this.spriteIndex++;
            if (this.spriteIndex === this.spriteImages.length) {
                this.spriteIndex = 0;
            }
        }, this.frameTime);
    }

    stop(): void {
        clearInterval(this.animInterval);
    }

    static fromSpriteURL(
        url: string,
        imgOptions?: Partial<ImageProps>
    ): Promise<Sprite> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                resolve(new Sprite(img, imgOptions));
            };
            img.onerror = reject;
            img.src = url;
        });
    }
}

classRegistry.setClass(Sprite);
